/**
 * Pretplata plan picker (A5, Luka's WEB5): the four paid plans as one native radio group,
 * 3 months preselected by the page, no "most popular" badge (R3). Plan names are keyed by
 * plan id; prices come from the API catalog and are formatted "26,99 €" (formatPrice).
 *
 * RenewalDisclosure is the renewal and cancellation text shown next to the CTA (Q6). Stripe
 * Checkout has no Montenegrin, Serbian, Bosnian, Macedonian or Albanian, so for those
 * languages this is the only disclosure the buyer reads.
 */
import { cn } from '@/utils/cn';
import { formatApproxPrice, formatPrice, getPlanPickerTranslation, translatePlanName } from '@/utils';
import { PLAN_IDS, PLAN_MONTHS, type PlanId } from '@/config/plans';
import type { PlanPickerTranslationKey } from '@/utils/translations';
import { useLanguage } from '@/store';
import type { PricingPlan } from '@/types';

interface PlanPickerProps {
  /** The API catalog (`GET /api/pricing/plans`), keyed by plan id. */
  plans: Readonly<Record<string, PricingPlan>>;
  selected: PlanId;
  onSelect: (planId: PlanId) => void;
  /** id of the heading that names the group. */
  labelledBy: string;
}

const RENEWAL_KEYS: Readonly<Record<PlanId, PlanPickerTranslationKey>> = {
  basic_monthly: 'renewsMonthly',
  quarterly: 'renewsQuarterly',
  semi_annual: 'renewsSemiAnnual',
  annual: 'renewsAnnual',
};

function fill(template: string, price: string): string {
  return template.replace('{price}', price);
}

export function PlanPicker({ plans, selected, onSelect, labelledBy }: PlanPickerProps) {
  const language = useLanguage();
  const t = (key: PlanPickerTranslationKey) => getPlanPickerTranslation(key, language);
  const available = PLAN_IDS.filter((id) => plans[id]);

  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className="space-y-3">
      {available.map((planId) => {
        const { price } = plans[planId];
        const months = PLAN_MONTHS[planId];
        const detail = months === 1 ? t('forOneMonth') : fill(t('perMonthApprox'), formatApproxPrice(price / months));
        const checked = planId === selected;
        return (
          <label
            key={planId}
            className={cn(
              'flex min-h-[4.5rem] cursor-pointer items-center gap-3 rounded-xl border bg-card p-4 transition-colors',
              'hover:border-primary/60 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring',
              checked && 'border-primary bg-primary/10',
            )}
          >
            <input
              type="radio"
              name="plan"
              value={planId}
              checked={checked}
              onChange={() => onSelect(planId)}
              className="h-5 w-5 shrink-0 accent-primary"
            />
            <span className="min-w-0 flex-1">
              <span className="block font-semibold">{translatePlanName(planId, language)}</span>
              <span className="block text-sm text-muted-foreground">{detail}</span>
            </span>
            <span className="shrink-0 whitespace-nowrap text-lg font-semibold">{formatPrice(price)}</span>
          </label>
        );
      })}
    </div>
  );
}

interface RenewalDisclosureProps {
  id: string;
  planId: PlanId;
  price: number;
  className?: string;
}

/** "Naplaćuje se 26,99 € svaka 3 mjeseca." + Luka's Q6 renewal and cancellation terms. */
export function RenewalDisclosure({ id, planId, price, className }: RenewalDisclosureProps) {
  const language = useLanguage();
  return (
    <p id={id} className={cn('text-sm text-muted-foreground', className)}>
      {fill(getPlanPickerTranslation(RENEWAL_KEYS[planId], language), formatPrice(price))}{' '}
      {getPlanPickerTranslation('renewalTerms', language)}
    </p>
  );
}
